/**
 * Honeywell + Pyronix + Risco + Texecom PIR / motion detector catalog.
 *
 * Verified against manufacturer pages on 2026-05-15:
 *   - pyronix.com/products/<id>
 *   - riscogroup.com/en/rscm_product/<id>
 *   - texe.com/products/<id> and branding.texe.com
 *   - Honeywell: a few SKUs use mirrored datasheets (geoarm.com /
 *     alarmclub.com / alarmgrid.com) because the Honeywell building
 *     solutions catalog page does not always expose full spec sheets
 *     — URLs cited per entry.
 *
 * Category note: all wall-mounted PIRs use the existing
 * `outdoor-wall-pir` category (the schema lacks an 'indoor-wall-pir'
 * variant; the existing Optex entries follow the same convention for
 * wall-mount form factor).
 */

import type { DetectionSensorCatalogEntry } from './detectionSensorCatalog';

export const HONEYWELL_PYRONIX_RISCO_TEXECOM_PIR: DetectionSensorCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // Honeywell — wall-mount PIR / dual-tech
  // ─────────────────────────────────────────────────────────────

  {
    id: 'honeywell-is2535',
    manufacturer: 'Honeywell',
    model: 'IS2535',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 11,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.3,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    operatingTempMinC: -10,
    operatingTempMaxC: 55,
    powerVMin: 8.5,
    powerVMax: 15.4,
    currentMaTypical: 17,
    outputType: 'NC_relay',
    datasheetUrl: 'https://www.alarmgrid.com/documents/honeywell-is2535-spec-sheet',
    tagline: 'Indoor wall PIR 11×12 m with selectable pet immunity up to 36 kg (80 lb)',
  },

  {
    id: 'honeywell-is3050v',
    manufacturer: 'Honeywell',
    model: 'IS3050V',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 22,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    operatingTempMinC: -10,
    operatingTempMaxC: 55,
    powerVMin: 9.0,
    powerVMax: 15.0,
    outputType: 'NC_relay',
    datasheetUrl: 'https://www.geoarm.com/is3050v-honeywell-home-hardwired-pir-motion-detector.html',
    tagline: 'Wall PIR 16×22 m with selectable pet immunity up to 45 kg (100 lb)',
  },

  {
    id: 'honeywell-dt8035v',
    manufacturer: 'Honeywell',
    model: 'DT8035V',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    petImmune: true,
    operatingTempMinC: -10,
    operatingTempMaxC: 55,
    powerVMin: 9.0,
    powerVMax: 15.0,
    currentMaTypical: 15,
    outputType: 'NC_relay',
    datasheetUrl: 'https://www.alarmclub.com/dt8035-honeywell-dual-tec-motion-detector.html',
    tagline: 'DUAL TEC PIR + microwave 12×17 m with 45 kg pet immunity',
  },

  {
    id: 'honeywell-dt8050a',
    manufacturer: 'Honeywell',
    model: 'DT8050A',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 22,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    petImmune: true,
    operatingTempMinC: -10,
    operatingTempMaxC: 55,
    powerVMin: 9.0,
    powerVMax: 15.0,
    currentMaTypical: 15,
    outputType: 'NC_relay',
    datasheetUrl: 'https://www.geoarm.com/dt8050-honeywell-dual-tec-motion-detector.html',
    tagline: 'DUAL TEC PIR + microwave 16×22 m with anti-mask',
  },

  // ─────────────────────────────────────────────────────────────
  // Pyronix — KX series + Octopus DQ ceiling
  // ─────────────────────────────────────────────────────────────

  {
    id: 'pyronix-kx15dt',
    manufacturer: 'Pyronix',
    model: 'KX15DT',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    datasheetUrl: 'https://www.pyronix.com/products/kx15dt',
    tagline: '15 m dual-tech PIR + X-band microwave with Blue Wave Technology',
  },

  {
    id: 'pyronix-kx10dtp',
    manufacturer: 'Pyronix',
    model: 'KX10DTP',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 10,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    petImmune: true,
    datasheetUrl: 'https://www.pyronix.com/products/kx10dtp',
    tagline: '10 m dual-tech with intelligent pet immunity up to 24 kg',
  },

  {
    id: 'pyronix-xdh10tt-am',
    manufacturer: 'Pyronix',
    model: 'XDH10TT-AM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 10,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.2,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 70,
    powerVMin: 9,
    powerVMax: 16,
    currentMaTypical: 24,
    outputType: 'solid_state',
    datasheetUrl: 'https://www.pyronix.com/products/xdh10tt-am',
    tagline: 'Outdoor triple-tech (2× PIR + microwave) with anti-mask, IP55, 10 kg pet immunity',
  },

  {
    id: 'pyronix-octopus-dq',
    manufacturer: 'Pyronix',
    model: 'Octopus DQ',
    pattern: 'full',
    category: 'indoor-ceiling-pir',
    rangeM: 12,
    hfovDeg: 360,
    mountingHeightDefaultM: 3.0,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 4.0,
    mounting: ['ceiling'],
    detectionMethod: 'pir',
    operatingTempMinC: -10,
    operatingTempMaxC: 40,
    powerVMin: 9,
    powerVMax: 16,
    currentMaTypical: 15,
    outputType: 'NC_relay',
    datasheetUrl: 'https://www.pyronix.com/products/octopus-dq',
    tagline: '360° ceiling-mount quad-element PIR, 12 m diameter at 4 m mounting height',
  },

  {
    id: 'pyronix-kx10dtp-we',
    manufacturer: 'Pyronix',
    model: 'KX10DTP-WE',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 10,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    petImmune: true,
    batteryOperated: true,
    datasheetUrl: 'https://www.pyronix.com/products/kx10dtp-we',
    tagline: 'Wireless two-way dual-tech for Enforcer, 10 m range, 20 kg pet immunity',
  },

  // ─────────────────────────────────────────────────────────────
  // Risco — iWISE + BWare + Industrial LuNAR + WatchOUT + Beyond
  // ─────────────────────────────────────────────────────────────

  {
    id: 'risco-iwise-rk815',
    manufacturer: 'Risco',
    model: 'iWISE DT (RK815DTG3)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    datasheetUrl: 'https://riscogroup.com/en/rscm_product/iwise/',
    tagline: '15 m dual-tech with Anti-Cloak Technology and IR anti-mask, Grade 3',
  },

  {
    id: 'risco-bware-rk515dt',
    manufacturer: 'Risco',
    model: 'BWare DT (RK515DTG3)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    currentMaTypical: 16,
    datasheetUrl: 'https://www.sourcesecurity.com/datasheets/risco-group-rk515dtgl-bware-dt-grade-2-intruder-detector/co-1403-ga/BWare%20brochure%20EN-LR.pdf',
    tagline: 'Commercial 15 m dual-tech with K-band MW and Anti-Cloak Technology, Grade 3',
  },

  {
    id: 'risco-industrial-lunar-rk200dt',
    manufacturer: 'Risco',
    model: 'Industrial LuNAR DT AM (RK200DTG3)',
    pattern: 'full',
    category: 'indoor-ceiling-pir',
    rangeM: 18,
    hfovDeg: 360,
    mountingHeightDefaultM: 5.0,
    mountingHeightMinM: 3.7,
    mountingHeightMaxM: 8.6,
    mounting: ['ceiling'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    datasheetUrl: 'https://riscogroup.com/en/rscm_product/industrial-lunar-ceiling-detector/',
    tagline: 'High-ceiling 360° dual-tech for industrial sites, 18 m diameter, Anti-Cloak',
  },

  {
    id: 'risco-watchout-dt-rk315dt',
    manufacturer: 'Risco',
    model: 'WatchOUT DT eXtreme (RK315DT)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 1.5,
    mountingHeightMinM: 1.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    ipRating: 'IP65',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9,
    powerVMax: 16,
    currentMaTypical: 35,
    datasheetUrl: 'https://www.homecontrols.com/homecontrols/products/pdfs/RK-Risco/RK315DT-Manual.pdf',
    tagline: 'Outdoor 15 m / 90° dual-tech, IP65, optional 23 m long-range lens, Grade 3',
  },

  {
    id: 'risco-watchout-pir-rk315pr',
    manufacturer: 'Risco',
    model: 'WatchOUT PIR (RK315PR)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 1.8,
    mountingHeightMinM: 1.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    petImmune: true,
    ipRating: 'IP65',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9,
    powerVMax: 16,
    datasheetUrl: 'https://www.sourcesecurity.com/risco-group-watchout-pir-technical-details.html',
    tagline: 'Outdoor dual-PIR 12 m / 90° with Digital Correlation, IP65',
  },

  {
    id: 'risco-beyond-dt-rk350dt',
    manufacturer: 'Risco',
    model: 'Beyond DT (RK350DT)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.2,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    ipRating: 'IP54',
    datasheetUrl: 'https://riscogroup.com/en/rscm_product/beyond-outdoor-detector/',
    tagline: 'Outdoor 12 m dual-tech (2× PIR + 2× K-band MW) with active-IR anti-mask, IP54',
  },

  // ─────────────────────────────────────────────────────────────
  // Texecom — Capture + Premier Elite + Premier External
  // ─────────────────────────────────────────────────────────────

  {
    id: 'texecom-capture-p15',
    manufacturer: 'Texecom',
    model: 'Capture P15',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.0,
    mountingHeightMinM: 1.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    operatingTempMinC: -10,
    operatingTempMaxC: 55,
    currentMaTypical: 9.3,
    datasheetUrl: 'https://www.texe.com/products/capture-p15/',
    tagline: '15 m / 90° dual-element PIR with PetWise mode up to 35 kg',
  },

  {
    id: 'texecom-capture-d20',
    manufacturer: 'Texecom',
    model: 'Capture D20',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 20,
    hfovDeg: 85,
    mountingHeightDefaultM: 2.0,
    mountingHeightMinM: 1.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    petImmune: true,
    datasheetUrl: 'https://www.texe.com/products/capture-d20/',
    tagline: '20 m / 85° quad PIR + K-band MW with PetWise and CloakWise modes',
  },

  {
    id: 'texecom-premier-elite-dt',
    manufacturer: 'Texecom',
    model: 'Premier Elite DT',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.0,
    mountingHeightMinM: 1.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    operatingTempMinC: -10,
    operatingTempMaxC: 55,
    datasheetUrl: 'https://branding.texe.com/uk/products/series/motion-detectors/premier-elite-series/',
    tagline: '15 m dual-tech with X-band Doppler MW and digital temp compensation, Grade 2',
  },

  {
    id: 'texecom-premier-elite-dt-w',
    manufacturer: 'Texecom',
    model: 'Premier Elite DT-W',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.0,
    mountingHeightMinM: 1.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    batteryOperated: true,
    operatingTempMinC: -10,
    operatingTempMaxC: 55,
    datasheetUrl: 'https://www.sourcesecurity.com/texecom-premier-elite-dt-w-intruder-detector-technical-details.html',
    tagline: 'Wireless 15 m dual-tech with Ricochet mesh for Premier Elite panels',
  },

  {
    id: 'texecom-premier-external-td-w',
    manufacturer: 'Texecom',
    model: 'Premier External TD-W',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 1.2,
    mountingHeightMinM: 1.0,
    mountingHeightMaxM: 1.4,
    mounting: ['wall'],
    detectionMethod: 'pir',
    batteryOperated: true,
    ipRating: 'IP65',
    datasheetUrl: 'https://www.texe.com/products/premier-external-td-w/',
    tagline: 'Outdoor wireless twin-mirrored PIR, selectable 12/8/5/2 m range, IP65',
  },
];
